<script setup lang="ts">
  // Actions of one caja movement on the narrow tier (phones/tablets), where
  // the row has no room for inline buttons. Opened via
  // showDynamicDialog<TCajaMovementAction | null>(..., 'caja-movement-actions');
  // resolves the chosen action, null when cancelled/dismissed.
  import { TCajaMovementAction } from '@/domain/caja/caja-movement-row';
  import { useModal } from '@/composition/use-modal';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type { ICajaMovementRowView } from './V2CajaMovementRow.vue';

  defineProps<{
    view: ICajaMovementRowView;
  }>();

  const modals = useModal();

  // The dialog unmounts on submit: nothing local is touched afterwards.
  function choose(action: TCajaMovementAction | null): void {
    modals.dialogSubmit('caja-movement-actions', action);
  }
</script>

<template>
  <div class="v2-caja-movement-actions-modal" :class="`tone-${view.tone}`">
    <div class="modal-header">
      <span class="header-tile">
        <PosUiIcon :name="view.icon" />
      </span>
      <span class="header-main">
        <span class="header-title">
          <span class="header-type">{{ view.typeLabel }}</span>
          <template v-if="view.methodLabel">
            <span class="header-dot">·</span>
            <span class="header-method">{{ view.methodLabel }}</span>
          </template>
        </span>
        <span class="header-meta">{{ view.metaText }}</span>
        <span v-if="view.description" class="header-desc">
          {{ view.description }}
        </span>
      </span>
      <span class="header-end">
        <span class="header-amount">{{ view.amountText }}</span>
        <PosUiBadge
          v-if="view.reimbursed"
          color="neutral"
          variant="soft"
          size="sm"
          icon="cash-refund"
        >
          {{ $t('posV2.payReimbursed') }}
        </PosUiBadge>
      </span>
    </div>

    <div class="modal-actions">
      <PosUiButton
        v-for="a in view.actionViews"
        :key="a.action"
        :color="a.color"
        :variant="a.variant"
        size="lg"
        block
        :icon="a.icon"
        @click="choose(a.action)"
      >
        {{ a.label }}
      </PosUiButton>
      <PosUiButton
        color="neutral"
        variant="ghost"
        size="lg"
        block
        @click="choose(null)"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-caja-movement-actions-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-items-start tw-gap-3;

      .header-tile {
        @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-200 tw-text-xl tw-text-neutral-700;
      }

      .header-main {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

        .header-title {
          @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-baseline tw-gap-1 tw-text-base tw-leading-5;

          .header-type {
            @apply tw-font-bold tw-text-neutral-900;
          }

          .header-dot {
            @apply tw-text-neutral-400;
          }

          .header-method {
            @apply tw-text-neutral-700;
          }
        }

        .header-meta {
          @apply tw-text-xs tw-tabular-nums tw-text-neutral-500;
        }

        .header-desc {
          @apply tw-mt-1 tw-text-sm tw-text-neutral-600;
        }
      }

      .header-end {
        @apply tw-flex tw-flex-none tw-flex-col tw-items-end tw-gap-1;

        .header-amount {
          @apply tw-whitespace-nowrap tw-text-lg tw-font-bold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }

    &.tone-in .modal-header {
      .header-tile {
        @apply tw-bg-emerald-100 tw-text-emerald-700;
      }

      .header-end .header-amount {
        @apply tw-text-emerald-700;
      }
    }

    &.tone-out .modal-header {
      .header-tile {
        @apply tw-bg-red-100 tw-text-red-700;
      }

      .header-end .header-amount {
        @apply tw-text-red-700;
      }
    }

    &.tone-refunded .modal-header {
      .header-tile {
        @apply tw-text-neutral-500;
      }

      .header-main .header-title .header-type,
      .header-end .header-amount {
        @apply tw-line-through tw-text-neutral-400;
      }
    }

    .modal-actions {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
  }
</style>
